import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular-ivy';
import { BehaviorSubject, catchError, filter, map, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { HubspotFormDialogService } from 'src/app/components/hubspot-form-dialog/hubspot-form-dialog.service';
import { defaultSelfServeGating } from 'src/app/constants/billing.constants';
import { PaymentModelImp } from 'src/app/pages/new-billing/models/payment.model';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { AddonIds, AddonPricingService } from 'src/app/services/addon-pricing/addon-pricing.service';
import { BillingActionsService } from 'src/app/services/billing/billing-actions.service';
import { BillingApiService } from 'src/app/services/billing/billing-api.service';
import { DiscountEligibilityService } from 'src/app/services/discount-eligibility/discount-eligibility.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { ExperimentProps, SelfServeChatWithSalesGating } from 'src/app/services/growthbook/types';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { SelectCompany } from 'src/app/store/auth/auth.actions';
import { AuthSelectors } from 'src/app/store/auth/auth.selectors';
import { AuthState } from 'src/app/store/auth/auth.state';
import { DiscountApiData } from '../../pages/new-billing/types/api.types';
import { formatPriceForTooltip, formatPriceWithNotation } from '../../util/price-formatter.util';
import {
  AddonRemoveConfirmationDialogComponent,
  AddonRemoveConfirmationDialogResult
} from './addon-remove-confirmation-dialog.component';
import {
  AddonItem,
  AddonsResponse,
  normalizeAddonsListRemovalText,
  stripAddonRemovalRawFields
} from './addon.types';
import { PricingPlanCategory } from 'src/models';


@UntilDestroy()
@Component({
  selector: 'app-addons-center',
  templateUrl: './addons-page.component.html',
  styleUrls: ['./addons-page.component.scss'],
})
export class AddonsPageComponent implements OnDestroy, OnChanges, OnInit {
  private readonly destroy$ = new Subject<void>();
  private isInitialized = false;

  addOnsSubject = new BehaviorSubject<AddonItem[]>([]);
  public addOns$: Observable<AddonItem[]> = this.addOnsSubject.asObservable();
  allowAccess = false;
  hubspotFlowId: string | null = null;
  hoveredAddonIndex: number | null = null;
  confirmingPurchaseIndex: number | null = null;
  paymentErrorIndex: number | null = null;
  loadingBuyNowIndex: number | null = null;
  loadingRemoveIndex: number | null = null;
  currentErrorMessage: string | null = null;
  previousActionType: 'purchase' | 'remove' | null = null;
  showingDiscountInput: number | null = null;
  discountCodeValue = '';
  validatingDiscount: number | null = null;
  discountValidationError: string | null = null;
  availableDiscounts: DiscountApiData[] = [];
  appliedDiscount: { index: number, discount: DiscountApiData, code: string } | null = null;

  // Feature flags
  enableDiscountOnAddons = false;

  // Experiment properties
  private buyNowBtnExperiment: ExperimentProps | null = null;
  readonly mapRecurrenceKey = {
    year: 'yearly',
    yearly: 'yearly',
    annual: 'yearly',
    month: 'monthly',
    monthly: 'monthly',
  };

  @Input() isSummary = false;
  @Input() isChoosePlanPage = false;
  @Input() currentPlan: ConcretePlan;
  @Output() addonChange = new EventEmitter<void>();

  // Screen width tracking for responsive price notation
  screenWidth = 0;
  private readonly mobileBreakpoint = 800;

  private readonly company = this.store.selectSnapshot(AuthSelectors.company);
  private readonly getPricingPlanCategory = () =>
    this.store.selectSnapshot(AuthState.pricingPlanCategory) as PricingPlanCategory | undefined;

  get isPaddleProvider(): boolean {
    return this.company?.subscription?.provider?.toLowerCase() === 'paddle';
  }

  private isAddonAvailableForPurchase(addOn: AddonItem): boolean {
    return !addOn.purchased && addOn?.selfServeAddon && !addOn.isBeingRemoved;
  }

  private isAddonPurchaseSupported(): boolean {
    return this.isPaddleProvider && this.currentPlan?.billingMode !== 'invoicing';
  }

  // GrowthBook feature flag type for self-serve chat with sales gating
  private readonly getSelfServeGating = () => this.growthBookService.getFeatureValue<SelfServeChatWithSalesGating>('self-serve-chat-with-sales-gating', defaultSelfServeGating);

  get shouldShowChatWithSalesButton(): boolean {
    const userCount = this.company?.userCount ?? 0;
    const gating = this.getSelfServeGating();
    const max = Number(gating?.small?.max ?? 50);
    return userCount > max;
  }

  private normalizeString(value: unknown): string {
    if (value == null) return '';
    let str = '';
    if (typeof value === 'string') {
      str = value;
    } else if (typeof value === 'number') {
      str = String(value);
    }
    return str.toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, '');
  }

  private isSoftwareCostAddon(addOn: AddonItem): boolean {
    const ln = this.normalizeString(addOn?.linkname ?? (addOn as any)?.linkName);
    const title = this.normalizeString(addOn?.title);
    return ln === 'softwarecostinsights' || ln === 'softwarecost' || ln === 'sci' ||
      title === 'softwarecostinsights' || title === 'softwarecost' || title === 'sci' ||
      ln.includes('softwarecost') || title.includes('softwarecost');
  }

  private isBasicPlan(): boolean {
    return this.getPricingPlanCategory() === 'basic';
  }

  shouldShowUpgradeButton(addOn: AddonItem): boolean {
    return this.isSoftwareCostAddon(addOn) && this.isBasicPlan() && !addOn.purchased && !addOn.isBeingRemoved;
  }

  shouldShowBuyNowButton(addOn: AddonItem): boolean {
    if (this.shouldShowUpgradeButton(addOn)) {
      return false;
    }
    return this.isAddonAvailableForPurchase(addOn) && this.isAddonPurchaseSupported() && !addOn.isBeingRemoved;
  }

  getBuyNowButtonLabel(): string {
    const isVariantB = this.buyNowBtnExperiment?.variationid === 'B';

    return isVariantB
      ? this.translate.instant('billing.getAccessButton').toUpperCase()
      : this.translate.instant('billing.buyNowButton').toUpperCase();
  }

  shouldShowActivationInfo(addOn: AddonItem): boolean {
    // Only show activation info for purchased addons
    if (!addOn.purchased || !addOn?.activationIn) {
      return false;
    }

    const activationText = addOn?.activationIn.toLowerCase();
    return activationText.includes('24hrs') ||
           activationText.includes('24 hours') ||
           activationText.includes('24hr') ||
           activationText.includes('24 hour') ||
           activationText.includes('instantly');
  }

  isAddonActivated(addOn: AddonItem): boolean {
    // Check if the addon has been purchased
    if (!addOn.purchased || !this.currentPlan?.billingApiData?.billingDetails?.lineItems) {
      return false;
    }

    // Check if activation is automatic
    if (addOn?.activationIn?.toLowerCase()?.includes('instantly')) {
      return true;
    }

    // Find the line item for this addon
    const addonLineItem = this.currentPlan.billingApiData.billingDetails.lineItems.find(item => {
      // Check if this line item corresponds to the current addon
      if (!addOn.addonIds) return false;

      const addonIdValues = Object.values(addOn.addonIds);
      return addonIdValues.includes(item.planId);
    });

    // If no line item found, the addon is not activated yet
    if (!addonLineItem) {
      return false;
    }

    // If no createdAt timestamp, check if the addon has been purchased for more than 24 hours
    // This handles cases where createdAt might not be immediately available after purchase
    if (!addonLineItem.createdAt) {
      // For addons without createdAt, assume they need 24 hours to activate
      // unless they have automatic activation
      return false;
    }

    // Check if 24 hours have passed since the addon was created
    const createdAt = new Date(addonLineItem.createdAt);
    const now = new Date();
    const hoursDiff = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);

    return hoursDiff >= 24;
  }

  getActivationStatusText(addOn: AddonItem): string {
    // If addon is not purchased, don't show activation status
    if (!addOn.purchased) {
      return '';
    }

    if (this.isAddonActivated(addOn)) {
      // If addon is being removed, show "until <date>" using currentPlan expiry
      if (addOn.isBeingRemoved && this.currentPlan?.billingApiData?.billingDetails?.expiry) {
        const expiryDate = new Date(this.currentPlan.billingApiData.billingDetails.expiry);
        const formattedDate = this.datePipe.transform(expiryDate, 'mediumDate');
        return `Activated until ${formattedDate}`;
      }
      return 'Activated';
    }
    return 'Activate within 24hrs';
  }

  getActivationStatusIcon(addOn: AddonItem): string {
    // Only show activation icon for purchased addons
    if (!addOn.purchased) {
      return '';
    }

    if (this.isAddonActivated(addOn)) {
      return 'fiber_manual_record';
    }
    return '';
  }

  getAddonButtonText(addOn: AddonItem, index: number): string {
    if (addOn.isBeingRemoved) {
      return this.translate.instant('billing.addonsAddedCtaButton').toUpperCase();
    }
    if (this.isAddonHovered(index)) {
      return this.translate.instant('billing.addonsRemoveCtaButtonV2').toUpperCase();
    }
    return this.translate.instant('billing.addonsAddedCtaButton').toUpperCase();
  }

  getAddonButtonIcon(addOn: AddonItem, index: number): string {
    if (addOn.isBeingRemoved) {
      return 'check';
    }
    if (this.isAddonHovered(index)) {
      return 'close';
    }
    return 'check';
  }

  getTimeUntilActivation(addOn: AddonItem): string {
    if (!addOn.purchased || !this.currentPlan?.billingApiData?.billingDetails?.lineItems) {
      return '';
    }

    // Find the line item for this addon
    const addonLineItem = this.currentPlan.billingApiData.billingDetails.lineItems.find(item => {
      if (!addOn.addonIds) return false;

      const addonIdValues = Object.values(addOn.addonIds);
      return addonIdValues.includes(item.planId);
    });

    if (!addonLineItem?.createdAt) {
      return '';
    }

    const createdAt = new Date(addonLineItem.createdAt);
    const now = new Date();
    const hoursDiff = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);

    if (hoursDiff >= 24) {
      return '';
    }

    const remainingHours = Math.ceil(24 - hoursDiff);
    return `${remainingHours}h remaining`;
  }

  constructor(
    private readonly growthBookService: GrowthBookService,
    private readonly router: Router,
    private segment: SegmentService,
    private readonly hubspotFormDialog: HubspotFormDialogService,
    private readonly translate: TranslateService,
    private readonly billingApiService: BillingApiService,
    private readonly billingActionsService: BillingActionsService,
    private readonly store: Store,
    private readonly addonPricingService: AddonPricingService,
    private readonly localizedPricingService: LocalizedPricingService,
    private readonly datePipe: DatePipe,
    private readonly discountEligibilityService: DiscountEligibilityService,
    private readonly dialog: MatDialog,
  ) {
    this.initializeComponent();
  }

  onResize(event: UIEvent): void {
    const w = (event?.target as Window | null)?.innerWidth ??
      (globalThis?.window ? globalThis.window.innerWidth : this.screenWidth);
    this.screenWidth = w;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.addOnsSubject.complete();

    // Remove resize listener
    if (globalThis.window !== undefined) {
      globalThis.window.removeEventListener('resize', this.onResize.bind(this));
    }

    // Clean up any mobile tooltips
    this.hideMobileTooltip();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If currentPlan changes and we have addons data, reprocess the addons
    if (changes['currentPlan'] && this.addOnsSubject.value?.length > 0) {
      // Trigger a re-evaluation of addons that depend on currentPlan
      this.reprocessAddonsIfNeeded();

      // Force a UI update to refresh activation status
      setTimeout(() => {
        const currentAddons = this.addOnsSubject.value;
        if (currentAddons && currentAddons.length > 0) {
          this.addOnsSubject.next([...currentAddons]);
        }
      }, 100);
    }
  }

  ngOnInit(): void {
    // Initialize experiment properties
    this.buyNowBtnExperiment = this.growthBookService.getFeatureValue<ExperimentProps>('GT2-4286');

    // Initialize screen width safely for SSR
    if (globalThis.window !== undefined) {
      this.screenWidth = globalThis.window.innerWidth;
    }

    // Set up resize listener
    if (globalThis.window !== undefined) {
      globalThis.window.addEventListener('resize', this.onResize.bind(this));
    }

    // Set up a timer to check activation status every hour
    this.setupActivationStatusTimer();

    // Listen for currentPlan changes to update activation status
    this.setupCurrentPlanListener();

    // Subscribe to the discount feature flag reactively
    this.growthBookService.getFeature<boolean>('enable-discount-on-addons', false)
      .pipe(
        switchMap(enabled => {
          if (!enabled) {
            return of({ enabled: false, loadDiscounts: false });
          }

          const eligibilityFlag = this.growthBookService.getFeatureValue<boolean>('add-coupons-to-companies', false);
          if (!eligibilityFlag) {
            return of({ enabled: true, loadDiscounts: true });
          }

          const companyId = this.store.selectSnapshot(AuthSelectors.company)?.id;
          if (!companyId) {
            Sentry.captureMessage('Discount eligibility check skipped due to missing companyId', {
              level: 'warning',
              tags: { feature: 'add-coupons-to-companies' },
            });
            return of({ enabled: false, loadDiscounts: false });
          }

          return this.discountEligibilityService.isDiscountAllowed(companyId).pipe(
            map(isAllowed => ({ enabled: isAllowed, loadDiscounts: isAllowed })),
            catchError(error => {
              Sentry.captureException(error, {
                level: 'error',
                tags: { feature: 'add-coupons-to-companies' },
                extra: { companyId },
              });
              return of({ enabled: false, loadDiscounts: false });
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ enabled, loadDiscounts }) => {
        this.enableDiscountOnAddons = enabled;

        if (enabled && loadDiscounts) {
          this.loadAvailableDiscounts();
        } else {
          this.availableDiscounts = [];
        }
      });
  }

  private setupCurrentPlanListener(): void {
    // This will help handle cases where currentPlan is updated after purchase
    // and we need to refresh the activation status
    if (this.currentPlan) {
      // Force an initial check of activation status
      this.forceRefreshActivationStatus();
    }
  }

  private reprocessAddonsIfNeeded(): void {
    // Get the current addons and reprocess them with the new currentPlan
    const currentAddons = this.addOnsSubject.value;
    if (currentAddons && currentAddons.length > 0) {
      const reprocessedAddons = this.processAddons(currentAddons);
      this.addOnsSubject.next(reprocessedAddons);
    }
  }

  /**
   * Recalculates addon prices based on the current plan's subscription recurrence
   * This method can be called when the plan changes to update pricing dynamically
   */
  public recalculateAddonPrices(): void {
    const currentAddons = this.addOnsSubject.value;
    if (currentAddons && currentAddons.length > 0) {
      const updatedAddons = currentAddons.map(addon => {
        const dynamicPricing = this.addonPricingService.calculateAddonPricing(
          addon.addonIds,
          this.currentPlan,
          addon.price,
          addon.subtitle,
        );
        return {
          ...addon,
          calculatedPrice: dynamicPricing.calculatedPrice,
          calculatedSubtitle: dynamicPricing.calculatedSubtitle,
        };
      });
      this.addOnsSubject.next(updatedAddons);
    }
  }

  private initializeComponent(): void {
    const viewAddonsPageGuard = this.growthBookService.getFeatureValue('addons-page', false);

    if (!viewAddonsPageGuard) {
      this.router.navigate(['/billing']);
      return;
    }

    this.allowAccess = true;
    this.segment.page('', 'Viewed AddOns Page', { path: '/addons' });
    this.initAddOns();
  }

  private initAddOns(): void {
    if (this.isInitialized) {
      return; // Prevent multiple initializations
    }
    this.isInitialized = true;

    // Wait for company data to be available before proceeding
    this.store.select(AuthSelectors.company).pipe(
      filter(company => !!company), // Wait until company is loaded
      take(1), // Take only the first emission
      switchMap(company => {
        // Now we have company data, proceed with addons initialization
        return this.growthBookService.getFeature('addons-list');
      }),
      catchError((error) => {
        Sentry.captureException(error, {
          extra: { message: 'Failed to get addons list from GrowthBook' },
        });
        return of(null);
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (feature) => {
        if (!feature) {
          return;
        }

        try {
          const response: AddonsResponse = this.growthBookService.getFeatureValue('addons-list', {
            flowId: '',
            addons: [],
          });

          if (this.isValidAddonsResponse(response)) {
            const processedAddons = this.processAddons(response.addons);
            this.addOnsSubject.next(processedAddons);
            this.hubspotFlowId = response.flowId || null;
          } else {
            this.handleInvalidResponse(response);
          }
        } catch (error) {
          this.handleProcessingError(error);
        }
      },
      error: (error) => {
        this.handleSubscriptionError(error);
      },
    });
  }

  private isValidAddonsResponse(response: any): response is AddonsResponse {
    return (
      response?.addons &&
      Array.isArray(response.addons) &&
      response.addons.length > 0
    );
  }

  private processAddons(addons: AddonItem[]): AddonItem[] {
    const subscriptionPlanIds = this.getSubscriptionPlanIds();

    return addons.map(addon => this.processAddon(addon, subscriptionPlanIds));
  }

  private processAddon(addon: AddonItem, subscriptionPlanIds: string[]): AddonItem {
    // Check if addon has a line item with action 'remove'
    const isBeingRemoved = this.isAddonBeingRemoved(addon.addonIds);

    const gb = addon as AddonItem & Record<string, unknown>;
    const removalText = normalizeAddonsListRemovalText(gb);
    const addonCore = stripAddonRemovalRawFields(gb);

    const processedAddon = {
      ...addonCore,
      title: PaymentModelImp.getCompleteTitle(addon.title),
      purchased: this.addonPricingService.isAddonPurchased(addon.addonIds, subscriptionPlanIds) || isBeingRemoved,
      ...(isBeingRemoved && { isBeingRemoved }),
    };

    // Calculate dynamic pricing based on current plan's recurrence
    const dynamicPricing = this.addonPricingService.calculateAddonPricing(
      addon.addonIds,
      this.currentPlan,
      addon.price,
      addon.subtitle,
    );

    return {
      ...processedAddon,
      calculatedPrice: dynamicPricing.calculatedPrice,
      calculatedSubtitle: dynamicPricing.calculatedSubtitle,
      ...(removalText !== undefined ? { removalText } : {}),
    };
  }

  private getSubscriptionPlanIds(): string[] {
    return this.addonPricingService.getSubscriptionPlanIds(this.currentPlan);
  }

  private isAddonBeingRemoved(addonIds: AddonIds): boolean {
    if (!addonIds || !this.currentPlan?.billingApiData?.billingDetails?.lineItems) {
      return false;
    }

    const addonIdValues = Object.values(addonIds);
    return this.currentPlan.billingApiData.billingDetails.lineItems.some(item =>
      addonIdValues.includes(item.planId) && item.action === 'remove',
    );
  }

  private getTerms(addonIds: AddonIds): keyof AddonIds | null {
    return this.addonPricingService.getTerms(addonIds, this.currentPlan);
  }

  private handleInvalidResponse(response: any): void {
    Sentry.captureMessage('Invalid addons response structure from GrowthBook.', {
      level: 'error',
      extra: {
        response,
        expectedStructure: 'AddonsResponse with addons array',
      },
    });

    // Set empty array to prevent UI issues
    this.addOnsSubject.next([]);
  }

  private handleProcessingError(error: any): void {
    Sentry.captureException(error, {
      extra: {
        message: 'Error processing addons data',
        component: 'AddonsPageComponent',
      },
    });

    // Set empty array to prevent UI issues
    this.addOnsSubject.next([]);
  }

  private handleSubscriptionError(error: any): void {
    Sentry.captureException(error, {
      extra: {
        message: 'Failed to get addons list from GrowthBook',
        component: 'AddonsPageComponent',
      },
    });

    // Set empty array to prevent UI issues
    this.addOnsSubject.next([]);
  }

  openAddonsLandingPage(event: Event, linkname: string): void {
    event.preventDefault();
    // Clear hover state when button is clicked
    this.hoveredAddonIndex = null;
    this.trackAddonsLandingPageClick(linkname, 'chat-with-sales');

    try {
      this.hubspotFormDialog.openContactSalesDialog({
        title: this.translate.instant('billing.addonsCtaButton'),
        formKind: 'addon',
        featureInterest: linkname,
      }).subscribe(() => {
        // Ensure focus is properly managed after dialog closes
        // This helps prevent the cdk-focus class from persisting
        setTimeout(() => {
          const activeElement = document.activeElement as HTMLElement;
          if (activeElement?.classList.contains('contact-sales-btn')) {
            activeElement.blur();
          }
        }, 0);
      });
    } catch (error) {
      this.handleHubspotDialogError(error);
    }
  }

  private handleHubspotDialogError(error: unknown): void {
    // Fallback to opening the URL
    const url = 'https://www.timedoctor.com/add-ons?&utm_campaign=c0078_add-on%20campaign&utm_source=product&utm_medium=page&utm_content=billing-page';
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

    if (newWindow) {
      newWindow.opener = null;
    }
  }

  private emitAddonChangeWithDelay(): void {
    // Add 2 second delay before emitting addonChange event
    setTimeout(() => {
      this.addonChange.emit();
    }, 1500);
  }

  private forceRefreshActivationStatus(): void {
    // Force a UI update to refresh activation status
    setTimeout(() => {
      const currentAddons = this.addOnsSubject.value;
      if (currentAddons && currentAddons.length > 0) {
        this.addOnsSubject.next([...currentAddons]);
      }
    }, 100);
  }

  buyNow(addonIds: AddonIds, index: number): void {
    if (!addonIds) {
      return;
    }

    // Track purchase intent
    const currentAddons = this.addOnsSubject.value;
    const addon = currentAddons[index];
    if (addon) {
      this.segment.trackInAppEvents('Addon Purchase Intent', {
        location: 'Addons Page',
        addonTitle: addon.title,
        addonId: Object.values(addonIds)[0], // Get the first available addon ID
      });
    }

    // Show purchase confirmation dialog first
    this.startPurchaseConfirmation(index);
  }

  goToChoosePlan(addOn?: AddonItem): void {
    try {
      this.segment.trackInAppEvents('Addon Upgrade Redirect', {
        location: 'Addons Page',
        addonTitle: addOn?.title || addOn?.linkname || 'software-cost-insights',
      });
    } catch { /* non-blocking */ }

    this.router.navigate(['/new-billing'], {
      queryParams: { choosePlan: 'yes' },
      queryParamsHandling: 'merge',
    });
  }

  confirmPurchase(addon: AddonItem, index: number): void {
    if (!addon.addonIds) {
      return;
    }

    const term = this.getTerms(addon.addonIds);
    if (!term) {
      return;
    }

    // Clear confirmation state and set loading state
    this.confirmingPurchaseIndex = null;
    this.loadingBuyNowIndex = index;

    // Get discountId if a valid discount is applied for this addon
    const discountId = this.getAppliedDiscountId(index);

    this.billingApiService.createAddOnLineItem(addon.addonIds[term], discountId)
      .pipe(
        catchError((error) => {
          Sentry.captureException(error, {
            extra: {
              message: 'Failed to create addon line item',
              addonIds: addon.addonIds,
              selectedTerm: term,
            },
          });

          // Clear loading state
          this.loadingBuyNowIndex = null;

          // Track purchase error event
          this.segment.trackInAppEvents('Addon Purchase Error', {
            location: 'Addons Page',
            addonId: addon.addonIds[term],
            addonTitle: addon.title,
            term,
            error: error?.error?.message || error?.message || 'Unknown error',
          });

          // Show error state widget for all errors
          const errorMessage = error?.error?.message || error?.message || 'Failed to add addon. Please try again.';
          this.showPaymentError(index, errorMessage, 'purchase');

          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          // Clear loading state
          this.loadingBuyNowIndex = null;

          if (response) {
            // Track successful purchase event
            this.segment.trackInAppEvents('Addon Purchased', {
              location: 'Addons Page',
              addonId: addon.addonIds[term],
              addonTitle: addon.title,
              term,
              price: addon.calculatedPrice || addon.price,
              billingPeriod: addon.calculatedSubtitle || addon.subtitle,
              discountId: discountId || null,
              discountApplied: !!discountId,
            });

            // Update the addon's purchased status to show Added button
            const currentAddons = this.addOnsSubject.value;
            const updatedAddons = currentAddons.map((currentAddon, addonIndex) => {
              if (addonIndex === index) {
                this.addAddonToCompany(currentAddon.addonIds[term]);
                return { ...currentAddon, purchased: true, isBeingRemoved: false };
              }
              return currentAddon;
            });
            this.addOnsSubject.next(updatedAddons);
            this.emitAddonChangeWithDelay();
            this.forceRefreshActivationStatus(); // Force refresh after successful purchase
          }
        },
      });
  }



  trackAddonsLandingPageClick(linkName: string, buttonType: 'buy-now' | 'chat-with-sales' = 'chat-with-sales'): void {
    this.segment.trackInAppEvents('Link Click', {
      linklocation: 'add-on Page',
      linkname: `${this.translate.instant(buttonType === 'buy-now' ? 'billing.buyNowButton' : 'billing.addonsCtaButton')} - ${linkName}`,
      linkurl: buttonType === 'buy-now' ? 'https://www.timedoctor.com/billing' : 'https://www.timedoctor.com/add-ons',
      pagename: 'addonpage',
      linktype: 'button',
      buttonType,
    });
  }

  onAddonButtonMouseEnter(index: number): void {
    this.hoveredAddonIndex = index;
  }

  onAddonButtonMouseLeave(): void {
    this.hoveredAddonIndex = null;
  }

  isAddonHovered(index: number): boolean {
    return this.hoveredAddonIndex === index;
  }

  // Blur after click so Angular Material / CDK focus styles do not persist.
  onAddonsAddedButtonClick(event: MouseEvent, index: number): void {
    (event.currentTarget as HTMLElement | null)?.blur();

    if (this.isAddonHovered(index)) {
      this.startRemoveConfirmation(index);
    }
  }

  isConfirmingPurchase(index: number): boolean {
    return this.confirmingPurchaseIndex === index;
  }

  isPaymentError(index: number): boolean {
    return this.paymentErrorIndex === index;
  }

  startPurchaseConfirmation(index: number): void {
    // Clear hover state when starting purchase confirmation
    this.hoveredAddonIndex = null;
    this.confirmingPurchaseIndex = index;
  }

  cancelPurchaseConfirmation(): void {
    // Track purchase cancellation
    if (this.confirmingPurchaseIndex !== null) {
      const currentAddons = this.addOnsSubject.value;
      const addon = currentAddons[this.confirmingPurchaseIndex];
      if (addon) {
        this.segment.trackInAppEvents('Addon Purchase Cancelled', {
          location: 'Addons Page',
          addonTitle: addon.title,
          addonId: addon.addonIds ? Object.values(addon.addonIds)[0] : null,
        });
      }

      // Clear any inputted coupon when cancel is clicked
      if (this.enableDiscountOnAddons) {
        const index = this.confirmingPurchaseIndex;
        // Check if there's an applied discount before clearing it
        const hadAppliedDiscount = this.appliedDiscount?.index === index;

        // Clear applied discount for this addon
        this.clearAppliedDiscount(index);

        // Hide discount input and clear discount code if it's showing for this addon
        if (this.showingDiscountInput === index) {
          this.hideDiscountInput();
        }

        // Also clear discount code value if there was an applied discount for this addon
        // (hideDiscountInput already clears it if input was showing, but we need to clear it if only discount was applied)
        if (hadAppliedDiscount && this.showingDiscountInput !== index) {
          this.discountCodeValue = '';
          this.discountValidationError = null;
        }
      }
    }

    this.hoveredAddonIndex = null;
    this.confirmingPurchaseIndex = null;
  }

  isLoadingBuyNow(index: number): boolean {
    return this.loadingBuyNowIndex === index;
  }

  isLoadingRemove(index: number): boolean {
    return this.loadingRemoveIndex === index;
  }

  startRemoveConfirmation(index: number): void {
    this.hoveredAddonIndex = null;
    const currentAddons = this.addOnsSubject.value;
    const addon = currentAddons[index];
    if (!addon) {
      return;
    }

    this.segment.trackInAppEvents('Addon Remove Intent', {
      location: 'Addons Page',
      addonTitle: addon.title,
      addonId: addon.addonIds ? Object.values(addon.addonIds)[0] : null,
    });

    const dialogRef = this.dialog.open(AddonRemoveConfirmationDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'addon-remove-confirmation-dialog-panel',
      data: { addonTitle: addon.title },
      autoFocus: false,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((result: AddonRemoveConfirmationDialogResult | undefined) => {
        if (result?.confirmed === true && result.churnReason) {
          this.confirmRemoveAddon(addon, index, result.churnReason);
          return;
        }
        this.segment.trackInAppEvents('Addon Remove Cancelled', {
          location: 'Addons Page',
          addonTitle: addon.title,
          addonId: addon.addonIds ? Object.values(addon.addonIds)[0] : null,
        });
      });
  }

  showPaymentError(index: number, errorMessage?: string, actionType?: 'purchase' | 'remove'): void {
    this.hoveredAddonIndex = null;
    this.confirmingPurchaseIndex = null;
    this.paymentErrorIndex = index;
    this.previousActionType = actionType || null;

    // Store the error message for display
    if (errorMessage) {
      this.currentErrorMessage = errorMessage;
    }
  }

  cancelPaymentError(): void {
    // Track payment error cancellation
    if (this.paymentErrorIndex !== null) {
      const currentAddons = this.addOnsSubject.value;
      const addon = currentAddons[this.paymentErrorIndex];
      if (addon) {
        this.segment.trackInAppEvents('Addon Payment Error Cancelled', {
          location: 'Addons Page',
          addonTitle: addon.title,
          addonId: addon.addonIds ? Object.values(addon.addonIds)[0] : null,
          previousActionType: this.previousActionType,
          errorMessage: this.currentErrorMessage,
        });
      }
    }

    this.paymentErrorIndex = null;
    this.currentErrorMessage = null;
    this.previousActionType = null;
  }

  onUpdatePaymentMethod(): void {
    // Track update payment method action
    if (this.paymentErrorIndex !== null) {
      const currentAddons = this.addOnsSubject.value;
      const addon = currentAddons[this.paymentErrorIndex];
      if (addon) {
        this.segment.trackInAppEvents('Addon Update Payment Method', {
          location: 'Addons Page',
          addonTitle: addon.title,
          addonId: addon.addonIds ? Object.values(addon.addonIds)[0] : null,
          previousActionType: this.previousActionType,
          errorMessage: this.currentErrorMessage,
        });
      }
    }

    // Open the checkout dialog for updating payment method
    const dialogOptions = {
      currentPlan: this.currentPlan,
      isUpdatingCard: true,
      trackingCtx: { location: 'Addons page - Payment error' },
    };

    this.billingActionsService.openCheckoutV2Dialog(dialogOptions);
    this.cancelPaymentError();
  }

  onTryAgain(): void {
    // Track try again action
    if (this.paymentErrorIndex !== null) {
      const currentAddons = this.addOnsSubject.value;
      const addon = currentAddons[this.paymentErrorIndex];
      if (addon) {
        this.segment.trackInAppEvents('Addon Try Again', {
          location: 'Addons Page',
          addonTitle: addon.title,
          addonId: addon.addonIds ? Object.values(addon.addonIds)[0] : null,
          previousActionType: this.previousActionType,
          errorMessage: this.currentErrorMessage,
        });
      }
    }

    // If there's a payment error index, trigger the appropriate confirmation based on previous action
    if (this.paymentErrorIndex !== null) {
      if (this.previousActionType === 'remove') {
        this.startRemoveConfirmation(this.paymentErrorIndex);
      } else {
        // Default to purchase confirmation for backward compatibility
        this.startPurchaseConfirmation(this.paymentErrorIndex);
      }
    }
    this.cancelPaymentError();
  }

  confirmRemoveAddon(addon: AddonItem, index: number, churnReason: string | null): void {
    if (!addon.addonIds) {
      return;
    }

    const term = this.getTerms(addon.addonIds);
    if (!term) {
      return;
    }

    // Set loading state
    this.loadingRemoveIndex = index;

    this.billingApiService.removeAddOnLineItem(addon.addonIds[term])
      .pipe(
        catchError((error) => {
          Sentry.captureException(error, {
            extra: {
              message: 'Failed to remove addon line item',
              addonIds: addon.addonIds,
              selectedTerm: term,
              addonTitle: addon.title,
            },
          });

          // Track remove error event
          this.segment.trackInAppEvents('Addon Remove Error', {
            location: 'Addons Page',
            addonId: addon.addonIds[term],
            addonTitle: addon.title,
            term,
            error: error?.error?.message || error?.message || 'Unknown error',
          });

          // Show error message in snackbar
          const errorMessage = error?.error?.message || error?.message || 'Failed to remove addon. Please try again.';
          this.showPaymentError(index, errorMessage, 'remove');

          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          // Clear loading state
          this.loadingRemoveIndex = null;

          if (response) {
            // Track successful remove event
            this.segment.trackInAppEvents('Addon Removed', {
              location: 'Addons Page',
              addonId: addon.addonIds[term],
              addonTitle: addon.title,
              term,
              churnReason,
            });

            // Reset UI state
            this.hoveredAddonIndex = null;

            // Update the addon's purchased status and set isBeingRemoved to true
            const currentAddons = this.addOnsSubject.value;
            const updatedAddons = currentAddons.map((currentAddon, addonIndex) => {
              if (addonIndex === index) {
                return { ...currentAddon, purchased: true, isBeingRemoved: true };
              }
              return currentAddon;
            });
            this.addOnsSubject.next(updatedAddons);
            this.emitAddonChangeWithDelay();
          }
        },
      });
  }

  private addAddonToCompany(addonId: string): void {
    if (this.company?.subscription?.addOns && !this.company.subscription.addOns.includes(addonId)) {
      const updatedCompany = {
        ...this.company,
        subscription: {
          ...this.company.subscription,
          addOns: [...this.company.subscription.addOns, addonId],
        },
      };
      this.store.dispatch(new SelectCompany(updatedCompany));
    }
  }

  private setupActivationStatusTimer(): void {
    // Check activation status every 5 minutes to update the UI more frequently
    // This helps with automatic activation and page refresh scenarios
    const checkInterval = setInterval(() => {
      // Force change detection by updating the addons subject
      const currentAddons = this.addOnsSubject.value;
      if (currentAddons && currentAddons.length > 0) {
        this.addOnsSubject.next([...currentAddons]);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    // Clean up the interval when component is destroyed
    this.destroy$.subscribe(() => {
      clearInterval(checkInterval);
    });
  }

  /**
   * Formats a price with localized currency for addon display
   * @param price - The price to format
   * @returns string - Formatted price with appropriate currency symbol
   */
  formatLocalizedPrice(price: number): string {
    const billingCurrency = this.currentPlan?.billingApiData?.billingDetails?.currency;

    // Priority 1: Use billing currency only if available and has matching price override
    if (billingCurrency && this.currentPlan) {
      // Check if currentPlan has a matching priceOverride for billing currency
      const hasMatchingOverride = this.currentPlan?.priceOverrides?.some(
        override => override.currencyCode === billingCurrency,
      );

      if (hasMatchingOverride) {
        return this.localizedPricingService.formatPriceWithCurrency(price, billingCurrency);
      }
    }

    // Priority 2: If no card is added, try to get price from price overrides based on user's country
    const noCardPrice = this.localizedPricingService.getLocalizedPriceForNoCard(this.currentPlan, price);
    if (noCardPrice) {
      return this.localizedPricingService.formatPriceWithCurrency(noCardPrice.price, noCardPrice.currency);
    }

    // Priority 3: Use geo-based pricing if localized pricing is enabled and no billing currency
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();
    if (isEnabled && !billingCurrency && this.currentPlan) {
      return this.localizedPricingService.formatLocalizedPriceForComponent(price, this.currentPlan);
    }

    // Priority 4: Fallback to standard formatting
    return this.localizedPricingService.formatLocalizedPriceForComponent(price, this.currentPlan);
  }

  /**
   * Gets the full price for tooltip display (without notation formatting)
   * @param addOn - The addon item
   * @returns string - Full price with currency symbol
   */
  getFullPriceForTooltip(addOn: AddonItem): string {
    const price = addOn.calculatedPrice || addOn.price;
    return formatPriceForTooltip(price);
  }

  /**
   * Determines if price notation should be shown based on screen width
   * @returns boolean - True if price notation should be shown
   */
  get shouldShowPriceNotation(): boolean {
    return this.screenWidth < this.mobileBreakpoint;
  }

  /**
   * Determines if tooltip should be shown for a price
   * @param addOn - The addon item
   * @returns boolean - True if tooltip should be shown
   */
  shouldShowTooltip(addOn: AddonItem): boolean {
    // Always show tooltip on mobile screens when price notation is active
    return this.shouldShowPriceNotation;
  }

  /**
   * Handle tooltip display on mobile devices
   * @param event - Click event
   * @param addOn - The addon item
   */
  showTooltipOnMobile(event: Event, addOn: AddonItem): void {
    if (this.shouldShowPriceNotation) {
      // Prevent default behavior
      event.preventDefault();
      event.stopPropagation();

      // Get the tooltip content directly from the addon
      const tooltipContent = this.getFullPriceForTooltip(addOn);

      // Create a custom tooltip-like element for mobile
      this.showMobileTooltip(event.target as HTMLElement, tooltipContent);
    }
  }

  /**
   * Show custom tooltip on mobile devices
   * @param target - The target element
   * @param tooltipContent - The tooltip content
   */
  private showMobileTooltip(target: HTMLElement, tooltipContent: string): void {
    // Remove any existing mobile tooltip
    this.hideMobileTooltip();

    if (!tooltipContent) {
      return;
    }

    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.className = 'mobile-tooltip';
    tooltip.textContent = tooltipContent;
    tooltip.style.cssText = `
      position: fixed;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 14px;
      z-index: 9999;
      pointer-events: none;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
      max-width: 280px;
      word-wrap: break-word;
    `;

    // Position the tooltip
    const rect = target.getBoundingClientRect();
    tooltip.style.left = `${rect.left + rect.width / 2}px`;
    tooltip.style.top = `${rect.top - 10}px`;
    tooltip.style.transform = 'translateX(-50%)';

    // Add to document
    document.body.appendChild(tooltip);

    // Auto-hide after 3 seconds
    setTimeout(() => {
      this.hideMobileTooltip();
    }, 3000);

    // Hide on touch/click anywhere
    const hideHandler = () => {
      this.hideMobileTooltip();
      document.removeEventListener('touchstart', hideHandler);
      document.removeEventListener('click', hideHandler);
    };

    setTimeout(() => {
      document.addEventListener('touchstart', hideHandler);
      document.addEventListener('click', hideHandler);
    }, 100);
  }

  /**
   * Hide mobile tooltip
   */
  private hideMobileTooltip(): void {
    const existingTooltip = document.querySelector('.mobile-tooltip');
    if (existingTooltip) {
      existingTooltip.remove();
    }
  }

  /**
   * Parses a price string with locale-aware decimal and thousand separators
   * @param priceString - The price string to parse
   * @returns number - The parsed numeric value
   */
  private parsePriceWithLocale(priceString: string): number {
    try {
      // Get user's locale
      const locale = Intl.NumberFormat().resolvedOptions().locale;

      // Detect decimal and thousand separators by formatting known numbers
      const decimalSeparator = new Intl.NumberFormat(locale).formatToParts(1.1).find(part => part.type === 'decimal')?.value || '.';
      const thousandSeparator = new Intl.NumberFormat(locale).formatToParts(1000).find(part => part.type === 'group')?.value || ',';

      // Remove currency symbols and whitespace
      const currencyRegex = /[₹$€£¥₽₩₪₫₨₦₡₱₴₸₼₾₿]/g;
      // eslint-disable-next-line
      let cleanPrice = priceString.replace(currencyRegex, '').trim();

      // Remove thousand separators
      if (thousandSeparator !== decimalSeparator) {
        // eslint-disable-next-line
        cleanPrice = cleanPrice.replace(new RegExp(`\\${thousandSeparator}`, 'g'), '');
      }

      // Replace locale decimal separator with standard dot
      if (decimalSeparator !== '.') {
        // eslint-disable-next-line
        cleanPrice = cleanPrice.replace(new RegExp(`\\${decimalSeparator}`, 'g'), '.');
      }

      // Parse the normalized string
      const numericValue = Number.parseFloat(cleanPrice);

      // Fallback to NaN handling
      return Number.isNaN(numericValue) ? 0 : numericValue;
    } catch {
      // Fallback to simple parsing if locale detection fails
      // eslint-disable-next-line
      const cleanPrice = priceString.replace(/[₹$€£¥₽₩₪₫₨₦₡₱₴₸₼₾₿,]/g, '').trim();
      const numericValue = Number.parseFloat(cleanPrice);
      return Number.isNaN(numericValue) ? 0 : numericValue;
    }
  }

  /**
   * Formats addon price with responsive notation
   * @param addOn - The addon item
   * @returns string - Formatted price with or without notation based on screen width
   */
  getFormattedAddonPrice(addOn: AddonItem): string {
    const price = addOn.calculatedPrice || addOn.price;

    if (!this.shouldShowPriceNotation) {
      return price;
    }

    // Extract numeric value from price string
    let numericPrice: number;
    if (typeof price === 'string') {
      numericPrice = this.parsePriceWithLocale(price);
    } else {
      numericPrice = price;
    }

    // Handle NaN case
    if (Number.isNaN(numericPrice)) {
      return price;
    }

    // Apply K/M notation formatting
    const formattedNumeric = formatPriceWithNotation(numericPrice);

    // Extract currency symbol from original price
    const currencyRegex = /[₹$€£¥₽₩₪₫₨₦₡₱₴₸₼₾₿]/;
    const currencyMatch = currencyRegex.exec(price);
    const currencySymbol = currencyMatch ? currencyMatch[0] : '$';

    return `${currencySymbol}${formattedNumeric}`;
  }

  /**
   * Shows the discount code input field for a specific addon
   * @param index - The index of the addon
   */
  showDiscountInput(index: number): void {
    // Track CTA click for discount code link
    const currentAddons = this.addOnsSubject.value;
    const addon = currentAddons[index];
    if (addon) {
      this.segment.trackInAppEvents('Link Click', {
        linklocation: 'Addons Page - Purchase Confirmation',
        linkname: 'Discount Code Link',
        linkurl: '',
        linktype: 'link',
        addonTitle: addon.title,
      });
    }

    this.showingDiscountInput = index;
    this.discountCodeValue = '';
    this.discountValidationError = null;
  }

  /**
   * Hides the discount code input field
   */
  hideDiscountInput(): void {
    this.showingDiscountInput = null;
    this.discountCodeValue = '';
    this.discountValidationError = null;
  }

  /**
   * Checks if discount input is showing for a specific addon
   * @param index - The index of the addon
   * @returns boolean - True if discount input is showing
   */
  isShowingDiscountInput(index: number): boolean {
    return this.showingDiscountInput === index;
  }

  /**
   * Loads available discounts from GrowthBook feature flag
   */
  private loadAvailableDiscounts(): void {
    try {
      // Get discount data from GrowthBook feature flag 'addon-coupons'
      const addonCoupons = this.growthBookService.getFeatureValue<DiscountApiData[]>('addon-coupons', []);

      if (Array.isArray(addonCoupons)) {
        this.availableDiscounts = addonCoupons;
      } else {
        console.warn('GrowthBook addon-coupons feature flag returned invalid data format');
        this.availableDiscounts = [];
      }
    } catch (error) {
      console.error('Failed to load discounts from GrowthBook:', error);
      this.availableDiscounts = [];
    }
  }

  /**
   * Validates a discount code against available discounts from GrowthBook
   * @param discountCode - The discount code to validate
   * @param addonIds - The addon IDs to check availability against
   * @returns DiscountApiData | null - The matching discount or null if not found
   */
  private validateDiscountCode(discountCode: string, addonIds: AddonIds): DiscountApiData | null {
    if (!discountCode.trim() || !this.availableDiscounts.length) {
      return null;
    }

    const normalizedCode = discountCode.trim().toUpperCase();
    const addonIdValues = Object.values(addonIds);
    return this.availableDiscounts.find(discount => {
      const discountCodeUpper = discount.discountId.toUpperCase();
      const isCodeMatch = discountCodeUpper === normalizedCode;

      // Check if discount is available for any of the addon plans
      const isPlanAvailable = !discount.availablePlans ||
        discount.availablePlans.some(planId => addonIdValues.includes(planId));

      // Check if discount is compatible with current subscription recurrence
      const isRecurrenceCompatible = this.isDiscountCompatibleWithCurrentRecurrence(discount, addonIds);

      return isCodeMatch && isPlanAvailable && isRecurrenceCompatible;
    }) || null;
  }

  /**
   * Checks if a discount is compatible with the current subscription recurrence
   * @param discount - The discount to check
   * @param addonIds - The addon IDs for different billing periods
   * @returns boolean - True if the discount is compatible with current recurrence
   */
  private isDiscountCompatibleWithCurrentRecurrence(discount: DiscountApiData, addonIds: AddonIds): boolean {
    if (!this.currentPlan || !discount.availablePlans) {
      return true; // If no current plan or no availablePlans, allow the discount
    }

    // Use the existing AddonPricingService to get the current term
    const currentTerm = this.addonPricingService.getTerms(addonIds, this.currentPlan);
    if (!currentTerm) {
      return true; // If we can't determine the current term, allow the discount
    }

    // Get the addon ID for the current term
    const currentTermAddonId = addonIds[currentTerm];
    if (!currentTermAddonId) {
      return true; // If no addon ID for current term, allow the discount
    }

    // Check if the discount's availablePlans includes the current term's addon ID
    const isCompatible = discount.availablePlans.includes(currentTermAddonId as any);

    return isCompatible;
  }

  /**
   * Applies the discount code
   * @param index - The index of the addon
   */
  applyDiscountCode(index: number): void {
    if (!this.discountCodeValue.trim()) {
      return;
    }

    const currentAddons = this.addOnsSubject.value;
    const addon = currentAddons[index];
    if (!addon?.addonIds) {
      return;
    }

    // Track CTA click for apply discount button
    this.segment.trackInAppEvents('Link Click', {
      linklocation: 'Addons Page - Purchase Confirmation',
      linkname: 'Apply Discount Code Button',
      linkurl: '',
      linktype: 'button',
      addonTitle: addon.title,
      discountCode: this.discountCodeValue,
    });

    // Set loading state
    this.validatingDiscount = index;
    this.discountValidationError = null;

    // Validate discount code
    const validatedDiscount = this.validateDiscountCode(this.discountCodeValue, addon.addonIds);

    if (validatedDiscount) {
      // Store the applied discount for success display
      this.appliedDiscount = {
        index,
        discount: validatedDiscount,
        code: this.discountCodeValue,
      };

      // Track successful discount code application
      this.segment.trackInAppEvents('Discount Code Applied', {
        location: 'Addons Page - Purchase Confirmation',
        addonTitle: addon.title,
        addonId: Object.values(addon.addonIds)[0],
        discountCode: this.discountCodeValue,
        discountId: validatedDiscount.discountId,
        discountType: validatedDiscount.type,
        discountValue: validatedDiscount.value,
      });

      // Hide the input after successful application
      this.hideDiscountInput();
    } else {
      // Track failed discount code application
      this.segment.trackInAppEvents('Discount Code Invalid', {
        location: 'Addons Page - Purchase Confirmation',
        addonTitle: addon.title,
        addonId: Object.values(addon.addonIds)[0],
        discountCode: this.discountCodeValue,
      });

      // Show error message
      this.discountValidationError = 'Invalid discount code. Please check and try again.';
    }

    // Clear loading state
    this.validatingDiscount = null;
  }

  /**
   * Checks if discount is being validated for a specific addon
   * @param index - The index of the addon
   * @returns boolean - True if discount is being validated
   */
  isValidatingDiscount(index: number): boolean {
    return this.validatingDiscount === index;
  }

  /**
   * Gets the discount validation error message
   * @returns string | null - The error message or null if no error
   */
  getDiscountValidationError(): string | null {
    return this.discountValidationError;
  }

  /**
   * Clears the discount validation error
   */
  clearDiscountValidationError(): void {
    this.discountValidationError = null;
  }

  /**
   * Checks if a discount is applied for a specific addon
   * @param index - The index of the addon
   * @returns boolean - True if discount is applied
   */
  isDiscountApplied(index: number): boolean {
    return this.appliedDiscount?.index === index;
  }

  /**
   * Determines if the confirm button should be disabled for a specific addon
   * Disabled when:
   * - Loading state is active
   * - OR when discount input is visible and no valid discount is applied
   * @param index - The index of the addon
   * @returns boolean - True if confirm button should be disabled
   */
  isConfirmDisabled(index: number): boolean {
    if (this.isLoadingBuyNow(index)) {
      return true;
    }

    // Disable if discount input is showing for this addon and no valid discount is applied
    if (this.enableDiscountOnAddons && this.isShowingDiscountInput(index) && !this.isDiscountApplied(index)) {
      return true;
    }

    return false;
  }

  /**
   * Gets the applied discount percentage for display
   * @param index - The index of the addon
   * @returns string - The discount percentage text
   */
  getAppliedDiscountText(index: number): string {
    if (!this.isDiscountApplied(index) || !this.appliedDiscount) {
      return '';
    }

    const { discount } = this.appliedDiscount;
    if (discount.type === 'percentage') {
      return `Discount applied: ${discount.value}% off`;
    } else if (discount.type === 'amount') {
      return `Discount applied: $${discount.value} off`;
    } else if (discount.type === 'amount_per_seat') {
      return `Discount applied: $${discount.value} per seat off`;
    }
    return 'Discount applied';
  }

  /**
   * Gets the original price for display (with strikethrough)
   * @param addOn - The addon item
   * @returns string - The original price
   */
  getOriginalPrice(addOn: AddonItem): string {
    return addOn.calculatedPrice || addOn.price;
  }

  /**
   * Gets the discounted price for display (bold)
   * @param addOn - The addon item
   * @param index - The index of the addon
   * @returns string - The discounted price
   */
  getDiscountedPrice(addOn: AddonItem, index: number): string {
    if (!this.isDiscountApplied(index) || !this.appliedDiscount) {
      return addOn.calculatedPrice || addOn.price;
    }

    const originalPrice = this.parsePriceWithLocale(addOn.calculatedPrice || addOn.price);
    const { discount } = this.appliedDiscount;

    let discountedPrice: number;
    if (discount.type === 'percentage') {
      discountedPrice = originalPrice * (1 - discount.value / 100);
    } else if (discount.type === 'amount') {
      discountedPrice = Math.max(0, originalPrice - discount.value);
    } else if (discount.type === 'amount_per_seat') {
      // For per-seat discounts, we'd need user count, but for now just apply the discount
      discountedPrice = Math.max(0, originalPrice - discount.value);
    } else {
      return addOn.calculatedPrice || addOn.price;
    }

    // Format the discounted price with the same currency symbol as original
    const currencyRegex = /[₹$€£¥₽₩₪₫₨₦₡₱₴₸₼₾₿]/;
    const currencyMatch = currencyRegex.exec(addOn.calculatedPrice || addOn.price);
    const currencySymbol = currencyMatch ? currencyMatch[0] : '$';

    return `${currencySymbol}${discountedPrice.toFixed(2)}`;
  }

  /**
   * Gets the confirmation text prefix (before the price)
   * @returns string - The translated prefix text
   */
  getConfirmationTextPrefix(): string {
    return this.translate.instant('billing.addonConfirmationTextPrefix');
  }

  /**
   * Gets the confirmation text suffix (after the price)
   * @returns string - The translated suffix text
   */
  getConfirmationTextSuffix(): string {
    return this.translate.instant('billing.addonConfirmationTextSuffix');
  }

  /**
   * Clears the applied discount for a specific addon
   * @param index - The index of the addon
   */
  clearAppliedDiscount(index: number): void {
    // Track CTA click for delete discount code link
    if (this.appliedDiscount?.index === index) {
      const currentAddons = this.addOnsSubject.value;
      const addon = currentAddons[index];
      if (addon) {
        this.segment.trackInAppEvents('Link Click', {
          linklocation: 'Addons Page - Purchase Confirmation',
          linkname: 'Delete Discount Code Link',
          linkurl: '',
          linktype: 'link',
          addonTitle: addon.title,
          discountCode: this.appliedDiscount.code,
        });
      }

      this.appliedDiscount = null;
    }
  }

  /**
   * Gets the discountId for a specific addon if a valid discount is applied
   * @param index - The index of the addon
   * @returns string | undefined - The discountId if a discount is applied, undefined otherwise
   */
  private getAppliedDiscountId(index: number): string | undefined {
    if (this.appliedDiscount?.index === index && this.appliedDiscount?.discount) {
      return this.appliedDiscount.discount.discountId;
    }
    return undefined;
  }

  /**
   * Checks if the discount feature is enabled
   * @returns boolean - True if discount feature is enabled
   */
  isDiscountFeatureEnabled(): boolean {
    return this.enableDiscountOnAddons;
  }

  /**
   * Checks if the discount section should be shown for a specific addon
   * @param index - The index of the addon
   * @returns boolean - True if discount section should be shown
   */
  shouldShowDiscountSection(index: number): boolean {
    return this.enableDiscountOnAddons;
  }

  /**
   * Handles discount code input key press events
   * @param event - Keyboard event
   * @param index - The index of the addon
   */
  onDiscountCodeKeyPress(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter') {
      this.applyDiscountCode(index);
    } else if (event.key === 'Escape') {
      this.hideDiscountInput();
    }
  }

  /**
   * Handles price keyboard events
   * @param event - Keyboard event
   * @param addOn - The addon item
   */
  onPriceKeyDown(event: KeyboardEvent, addOn: AddonItem): void {
    if (event.key === 'Enter' || event.key === ' ') {
      if (this.shouldShowPriceNotation) {
        this.showTooltipOnMobile(event, addOn);
      }
    }
  }

}
